<template>
  <div class="field">
    <div class="control base-input" :class="controlClasses">
      <input
        ref="inputEl"
        class="input"
        :class="inputClasses"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly || locked"
        :autocomplete="autocomplete"
        @input="$emit('update:modelValue', $event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="$emit('enter', $event)"
      />
      <span v-if="hasLeftIcon" class="icon is-left">
        <slot name="left">
          <i :class="normalizedIconLeft" />
        </slot>
      </span>
      <span v-if="hasRightContent" class="icon is-right">
        <slot name="right">
          <i v-if="locked" class="fa-solid fa-lock" />
          <i v-else-if="hasRightIcon" :class="normalizedIconRight" />
        </slot>
      </span>
    </div>
    <p v-if="typeof error === 'string' && error" class="help is-danger">
      {{ error }}
    </p>
  </div>
</template>

<script>
import { computed, ref } from 'vue'

function normalizeIcon(icon) {
  if (!icon) return []
  if (Array.isArray(icon)) return icon
  return icon.split(' ')
}

export default {
  name: 'BaseInput',
  props: {
    modelValue: { type: [String, Number], default: '' },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    locked: { type: Boolean, default: false },
    autocomplete: { type: String, default: null },
    iconLeft: { type: [String, Array], default: null },
    iconRight: { type: [String, Array], default: null },
    size: { type: String, default: null },
    rounded: { type: Boolean, default: false },
    error: { type: [Boolean, String], default: false },
  },
  emits: ['update:modelValue', 'focus', 'blur', 'enter'],
  setup(props, { emit, slots }) {
    const inputEl = ref(null)
    const isFocused = ref(false)

    const normalizedIconLeft = computed(() => normalizeIcon(props.iconLeft))
    const normalizedIconRight = computed(() => normalizeIcon(props.iconRight))

    const hasLeftIcon = computed(() => !!props.iconLeft || !!slots.left)
    const hasRightIcon = computed(() => !!props.iconRight)
    const hasRightContent = computed(
      () => props.locked || hasRightIcon.value || !!slots.right,
    )

    const controlClasses = computed(() => ({
      'has-icons-left': hasLeftIcon.value,
      'has-icons-right': hasRightContent.value,
    }))

    const inputClasses = computed(() => {
      const classes = []
      if (props.size) classes.push(`is-${props.size}`)
      if (props.rounded) classes.push('is-rounded')
      if (props.error) classes.push('is-danger')
      if (props.locked) classes.push('is-locked')
      if (!isFocused.value) classes.push('is-ellipsis')
      return classes
    })

    const onFocus = (e) => {
      isFocused.value = true
      emit('focus', e)
    }

    const onBlur = (e) => {
      isFocused.value = false
      emit('blur', e)
    }

    return {
      inputEl,
      isFocused,
      normalizedIconLeft,
      normalizedIconRight,
      hasLeftIcon,
      hasRightIcon,
      hasRightContent,
      controlClasses,
      inputClasses,
      onFocus,
      onBlur,
    }
  },
}
</script>

<style lang="scss" scoped>
.input.is-locked {
  background-color: var(--scheme-main-bis, hsl(0, 0%, 96%));
  color: var(--text-light, hsl(0, 0%, 48%));
}

.input.is-ellipsis {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

// Bulma's .control.has-icons-left .icon.is-left specificity [0,3,0] sets a fixed
// height that breaks when the input is taller than default. Match specificity to win.
.control.base-input.has-icons-left .icon.is-left,
.control.base-input.has-icons-right .icon.is-right {
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  top: 0;
  bottom: 0;
}

// Bulma sets pointer-events:none on .control .icon — re-enable for slotted content
.control .icon.is-right:has(button, a) {
  pointer-events: auto;
}
</style>
